import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const checkIsMobileDevice = () => window.innerWidth < 768 || (window.innerHeight <= 500 && window.innerWidth <= 950);

export const UserSidebar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(checkIsMobileDevice());
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [unreadBulletinCount, setUnreadBulletinCount] = useState(0);

    // Fetch unread bulletins count periodically
    useEffect(() => {
        if (!user) return;
        const fetchUnread = async () => {
            try {
                const headers: Record<string, string> = {};
                const token = localStorage.getItem('auth_token');
                if (token) headers['Authorization'] = `Bearer ${token}`;
                const res = await fetch('/api/bulletins/unread-count', { headers });
                const data = await res.json();
                if (data.success) {
                    setUnreadBulletinCount(data.count || 0);
                }
            } catch {
                // ignore
            }
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 30000);
        return () => clearInterval(interval);
    }, [user]);

    // Check if mobile on resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = checkIsMobileDevice();
            setIsMobile(mobile);
            if (!mobile) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const menuItems = [
        { path: '/bulletins', label: 'Bảng tin nội bộ', icon: 'fas fa-bullhorn', badge: unreadBulletinCount, disabled: false },
        { path: '/checkinout', label: 'Điểm danh', icon: 'fas fa-clock', disabled: false },
        { path: '/makeup-checkinout', label: 'Điểm danh bù', icon: 'fas fa-edit', disabled: false },
        { path: '/register', label: 'Đăng ký ca', icon: 'fas fa-calendar-alt', disabled: false },
        { path: '/salary', label: 'Thông tin lương', icon: 'fas fa-money-bill-wave', disabled: false },
        { path: '/nhap-hang', label: 'Nhập / kiểm kho', icon: 'fas fa-boxes', disabled: false },
    ];

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const sidebarWidth = isMobile ? (isMobileMenuOpen ? '250px' : '0') : (isCollapsed ? '70px' : '250px');

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <>
            {/* Mobile menu overlay */}
            {isMobile && isMobileMenuOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 999,
                    }}
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '52px',
                    background: '#0f172a',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    zIndex: 1001,
                    borderBottom: '1px solid #1e293b',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}>
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        style={{
                            background: 'transparent',
                            color: '#ffffff',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            padding: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        aria-label="Toggle Menu"
                    >
                        <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
                    </button>
                    <div style={{ fontWeight: 700, fontSize: '15px', letterSpacing: '-0.01em' }}>HomeTools</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {user?.isAdmin && (
                            <Link
                                to={user?.homePath || '/checkinout'}
                                style={{
                                    fontSize: '11px',
                                    color: '#ffffff',
                                    background: '#8d2a2c',
                                    padding: '3px 9px',
                                    borderRadius: '6px',
                                    textDecoration: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Quản trị
                            </Link>
                        )}
                        <div style={{ fontSize: '12px', color: '#94a3b8', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user?.name || user?.username}
                        </div>
                    </div>
                </div>
            )}

            <div style={{
                position: 'fixed',
                left: 0,
                top: isMobile ? '52px' : 0,
                width: sidebarWidth,
                height: isMobile ? 'calc(100vh - 52px)' : '100vh',
                background: '#0f172a',
                color: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1000,
                borderRight: '1px solid #1e293b',
                boxShadow: '1px 0 6px rgba(0, 0, 0, 0.25)',
                transition: 'width 0.25s ease',
                overflow: isMobile && !isMobileMenuOpen ? 'hidden' : 'visible'
            }}>
                {/* Header */}
                <div style={{
                    padding: isCollapsed && !isMobile ? '20px 8px' : '20px 20px',
                    borderBottom: '1px solid #1e293b',
                    background: '#090d16',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isCollapsed && !isMobile ? 'center' : 'flex-start'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isCollapsed && !isMobile ? 'center' : 'space-between',
                        width: '100%',
                    }}>
                        {(!isCollapsed || isMobile) && (
                            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8d2a2c' }}></span>
                                HomeTools
                            </h2>
                        )}
                        {isCollapsed && !isMobile && (
                            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#8d2a2c' }}></span>
                        )}
                        {!isMobile && (
                            <button
                                onClick={() => setIsCollapsed(!isCollapsed)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    padding: '5px',
                                    fontSize: '13px',
                                    borderRadius: '6px',
                                    transition: 'color 0.15s'
                                }}
                                title={isCollapsed ? 'Mở rộng' : 'Thu nhỏ'}
                            >
                                <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'}`}></i>
                            </button>
                        )}
                    </div>
                    {user && (!isCollapsed || isMobile) && (
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#94a3b8', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {user.name || user.username}
                            </div>
                            {user.isAdmin && (
                                <span style={{ fontSize: '10px', fontWeight: 700, background: 'rgba(141, 42, 44, 0.25)', color: '#fca5a5', padding: '1px 6px', borderRadius: '4px' }}>
                                    ADMIN
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Menu Items */}
                <nav style={{ flex: 1, padding: '16px 8px', overflowY: 'auto' }}>
                    {/* Admin Back Button */}
                    {user?.isAdmin && (
                        <Link
                            to={user?.homePath || '/checkinout'}
                            onClick={() => {
                                if (isMobile) setIsMobileMenuOpen(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: isCollapsed && !isMobile ? '12px 6px' : '10px 14px',
                                justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
                                color: '#ffffff',
                                textDecoration: 'none',
                                background: '#8d2a2c',
                                borderRadius: '8px',
                                transition: 'all 0.18s ease',
                                cursor: 'pointer',
                                marginBottom: '14px',
                                fontWeight: 600,
                                fontSize: '13px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#732022';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#8d2a2c';
                            }}
                            title={isCollapsed && !isMobile ? 'Trang quản trị' : undefined}
                        >
                            <i className="fas fa-shield-alt" style={{
                                marginRight: isCollapsed && !isMobile ? '0' : '10px',
                                width: '18px',
                                textAlign: 'center',
                                fontSize: '15px'
                            }}></i>
                            {(!isCollapsed || isMobile) && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                                    <span>Trang quản trị</span>
                                    <i className="fas fa-arrow-right" style={{ fontSize: '11px', opacity: 0.8 }}></i>
                                </div>
                            )}
                        </Link>
                    )}

                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path ||
                            (item.path === '/register' && location.pathname.startsWith('/register'));
                        return (
                            <Link
                                key={item.path}
                                to={item.disabled ? '#' : item.path}
                                onClick={(e) => {
                                    if (item.disabled) {
                                        e.preventDefault();
                                        alert('Tính năng này sẽ được phát triển sau');
                                    }
                                    if (isMobile) {
                                        setIsMobileMenuOpen(false);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: isCollapsed && !isMobile ? '12px 6px' : '10px 14px',
                                    justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
                                    color: item.disabled ? '#475569' : isActive ? '#ffffff' : '#cbd5e1',
                                    textDecoration: 'none',
                                    background: isActive ? 'rgba(141, 42, 44, 0.18)' : 'transparent',
                                    borderLeft: isActive ? '3px solid #8d2a2c' : '3px solid transparent',
                                    borderRadius: '8px',
                                    transition: 'all 0.15s ease',
                                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                                    opacity: item.disabled ? 0.5 : 1,
                                    position: 'relative',
                                    marginBottom: '4px',
                                    fontWeight: isActive ? 600 : 500,
                                    fontSize: '13.5px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!item.disabled && !isActive && !isMobile) {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                        e.currentTarget.style.color = '#ffffff';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!item.disabled && !isActive && !isMobile) {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#cbd5e1';
                                    }
                                }}
                                title={isCollapsed && !isMobile ? item.label : undefined}
                            >
                                <i className={item.icon} style={{
                                    marginRight: isCollapsed && !isMobile ? '0' : '12px',
                                    width: '18px',
                                    textAlign: 'center',
                                    fontSize: '15px',
                                    color: isActive ? '#fca5a5' : 'inherit'
                                }}></i>
                                {(!isCollapsed || isMobile) && (
                                    <>
                                        <span>{item.label}</span>
                                        {item.disabled && (
                                            <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#64748b' }}>Sắp có</span>
                                        )}
                                        {item.badge !== undefined && item.badge > 0 && (
                                            <span style={{
                                                marginLeft: 'auto',
                                                backgroundColor: '#8d2a2c',
                                                color: 'white',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                padding: '1px 7px',
                                                borderRadius: '10px'
                                            }}>
                                                {item.badge}
                                            </span>
                                        )}
                                    </>
                                )}
                                {isCollapsed && !isMobile && item.badge !== undefined && item.badge > 0 && (
                                    <span style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '8px',
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: '#8d2a2c',
                                        borderRadius: '50%'
                                    }}></span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Logout/Login Button */}
                <div style={{
                    padding: isCollapsed && !isMobile ? '16px 8px' : '16px 14px',
                    borderTop: '1px solid #1e293b',
                    marginBottom: '16px'
                }}>
                    <button
                        onClick={() => {
                            if (user) {
                                handleLogout();
                            } else {
                                navigate('/login');
                            }
                        }}
                        style={{
                            width: '100%',
                            padding: '10px',
                            background: 'rgba(255, 255, 255, 0.04)',
                            color: '#94a3b8',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: isCollapsed && !isMobile ? '15px' : '13px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.18s ease'
                        }}
                        onMouseEnter={(e) => {
                            if (!isMobile) {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.25)';
                                e.currentTarget.style.color = '#fca5a5';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isMobile) {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                                e.currentTarget.style.color = '#94a3b8';
                            }
                        }}
                        title={isCollapsed && !isMobile ? (user ? 'Đăng xuất' : 'Đăng nhập') : undefined}
                    >
                        <i className={user ? 'fas fa-sign-out-alt' : 'fas fa-sign-in-alt'} style={{ marginRight: isCollapsed && !isMobile ? '0' : '8px' }}></i>
                        {(!isCollapsed || isMobile) && <span>{user ? 'Đăng xuất' : 'Đăng nhập'}</span>}
                    </button>
                </div>
            </div>
        </>
    );
};
